import '../css/app.css';
import './bootstrap';

import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRoot } from 'react-dom/client';

import { ErrorFallback } from '@/Components/System/ErrorFallback';
import { initThemeInjector } from '@/Components/System/ThemeInjector';
import { SidebarProvider } from '@/hooks/use-sidebar';
import { registerServiceWorker } from '@/lib/pwa';
import { initSentry, Sentry, setSentryUser } from '@/lib/sentry';
import type { TenantUser } from '@/types';

initSentry();
initThemeInjector();
registerServiceWorker();

// Recover from stale Vite chunks after a deploy: if a dynamic page chunk
// 404s (build hashes shifted under us), reload once to fetch the new bundle.
// The 10s guard prevents a reload loop if the deploy is still in progress.
window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    const key = "vite:preloadReloadAt";
    const last = Number(sessionStorage.getItem(key) ?? "0");
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
});

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) =>
        resolvePageComponent<{ default: ResolvedComponent }>(
            `./Pages/${name}.tsx`,
            import.meta.glob<{ default: ResolvedComponent }>('./Pages/**/*.tsx'),
        ).then((module) => module.default),
    setup({ el, App, props }) {
        const pageProps = props.initialPage.props as Record<string, unknown>;
        const auth = pageProps.auth as { user?: TenantUser } | undefined;
        if (auth?.user) {
            setSentryUser(auth.user);
        }

        const root = createRoot(el);

        root.render(
            <Sentry.ErrorBoundary fallback={ErrorFallback}>
                <SidebarProvider>
                    <App {...props} />
                </SidebarProvider>
            </Sentry.ErrorBoundary>,
        );

        window.__carzelleBooted?.();
    },
    progress: {
        color: '#8c9438',
    },
});
